import { useEffect, useState } from "react";
import { StoryChartButton } from "./StoryChart";
import { api, DeskPlan, Directive, Manifest, Proposal, Ruling } from "../api";
import { fmtTime } from "../format";
import StonkyPortrait from "./StonkyPortrait";
import Ticker from "./Ticker";

const STATUS_BADGE: Record<string, string> = {
  executed: "bg-up/20 text-up",
  approved: "bg-up/20 text-up",
  escalated: "bg-gold/20 text-gold",
  peer_review: "bg-gold/20 text-gold",
  rejected: "bg-down/20 text-down",
  risk_blocked: "bg-down/20 text-down",
  draft: "bg-slate-700 text-slate-300",
  expired: "bg-slate-700 text-slate-500",
};

export default function Desk({ manifest }: { manifest: Manifest }) {
  const [plan, setPlan] = useState<DeskPlan | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [rulings, setRulings] = useState<Ruling[]>([]);
  // The board sits at 07:00 and the desk plan is written at 09:00, so for two
  // hours every morning this screen said only "No plan yet" — while the firm
  // had already convened and ratified the day's directives. An empty screen
  // during a window when real work HAS happened reads as a failure (Alan,
  // 2026-08-28: "what happened this morning, why no plan?").
  const [directives, setDirectives] = useState<Directive[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    try {
      setPlan(await api.get<DeskPlan | null>("/api/desk/plan"));
      setProposals(await api.get<Proposal[]>("/api/desk/proposals"));
      setRulings(await api.get<Ruling[]>("/api/desk/rulings"));
      setDirectives(await api.get<Directive[]>("/api/board/directives"));
    } catch (e) { setError((e as Error).message); }
  }

  async function run(kind: "run-plan" | "run-cycle") {
    setBusy(kind);
    setError(null);
    try {
      await api.post(`/api/desk/${kind}`);
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  const rulingByProposal = new Map(rulings.map((r) => [r.proposal_id, r]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Stonky&rsquo;s Desk</h1>
        <div className="flex gap-2">
          <button className="btn" onClick={() => run("run-plan")} disabled={busy !== null}>
            {busy === "run-plan" ? "Stonky is planning…" : "Stonky: plan the desk"}
          </button>
          <button className="btn btn-primary" onClick={() => run("run-cycle")} disabled={busy !== null}>
            {busy === "run-cycle" ? "Desk cycle running…" : "Run desk cycle"}
          </button>
        </div>
      </div>
      {error && <div className="text-down text-sm">{error}</div>}

      <StonkyPortrait />

      <div className="card">
        <h2 className="label">Today&rsquo;s desk plan</h2>
        {plan ? (
          <div className="space-y-3">
            {plan.plan.narrative && <p className="text-sm text-slate-300">{plan.plan.narrative}</p>}
            {plan.plan.hand && (
              <div className="text-xs text-slate-400 font-mono">
                Hand: ${plan.plan.hand.deployable.toLocaleString()} deployable
                {" · "}${plan.plan.hand.per_trade_cap.toLocaleString()} per-trade cap
                {" · "}${plan.plan.hand.risk_per_trade_usd.toLocaleString()} risked per ticket at the stop
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-2">
              {Object.entries(plan.plan.slices ?? {}).map(([junior, s]) => (
                <div key={junior} className="border border-edge rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-semibold">{manifest.agents[junior]?.title ?? junior}</span>
                    <span className="font-mono text-up">${s.budget_usd.toLocaleString()}</span>
                  </div>
                  <div className="text-slate-400 text-xs mt-1">{s.focus}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-slate-500 text-sm">
              No plan yet — Stonky writes the tickets at {manifest.schedule.desk_plan} ET
              {directives.length > 0
                ? ". The board has already sat, though; here is what it ratified this morning."
                : ", after the board sits at " + manifest.schedule.board_session + " ET."}
            </div>
            {/* Between the board (07:00) and the plan (09:00) the firm has
                decided the day's directives but written no tickets. Showing
                them turns a blank two-hour window into the actual state of
                play, instead of a screen that looks like nothing ran. */}
            {directives.length > 0 && (
              <ol className="space-y-1">
                {directives.map((d) => (
                  <li key={d.id} className="text-sm flex gap-2">
                    <span className="text-accent font-mono shrink-0">{d.seq}.</span>
                    <span className="text-slate-300">{d.text}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="label">Proposals</h2>
        <div className="space-y-2">
          {proposals.map((p) => {
            const ruling = rulingByProposal.get(p.id);
            return (
              <div key={p.id} className="border border-edge rounded-lg">
                <button className="w-full text-left px-3 py-2 flex items-center gap-3 text-sm"
                        onClick={() => setOpen(open === p.id ? null : p.id)}>
                  <span className={`badge ${STATUS_BADGE[p.status] ?? "bg-slate-700"}`}>{p.status}</span>
                  <Ticker symbol={p.symbol} className="font-mono font-bold" />
                  <span className={p.side === "long" ? "text-up" : "text-down"}>{p.side}</span>
                  <span className="font-mono">×{p.qty}</span>
                  <span className="text-slate-500 text-xs">{manifest.agents[p.junior]?.title ?? p.junior}</span>
                  <span className="ml-auto text-slate-600 text-xs">{fmtTime(p.created_at)}</span>
                </button>
                {open === p.id && (
                  <div className="px-3 pb-3 space-y-2 text-sm">
                    <p className="text-slate-300">{p.rationale}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-slate-400">
                      <span>entry: {p.entry_type}{p.limit_price ? ` @ ${p.limit_price}` : ""}</span>
                      <span>target: {p.exit_target ?? "—"}</span>
                      <span>stop: {p.exit_stop ?? "—"}</span>
                      <span>latest exit: {p.exit_time ?? "—"}</span>
                      <span>cites: {(p.cited_directives ?? []).join(", ") || "—"}</span>
                      <StoryChartButton symbol={p.symbol} entry={p.limit_price ?? null} stop={p.exit_stop ?? null} target={p.exit_target ?? null} side={p.side} label="proposal" />
                    </div>
                    {p.peer_feedback && Object.entries(p.peer_feedback).map(([peer, fb]) => (
                      <div key={peer} className="text-xs">
                        <span className={`badge ${fb.stance === "object" ? "bg-down/20 text-down" : "bg-up/20 text-up"}`}>peer {fb.stance}</span>
                        <span className="ml-2 text-slate-400">{fb.comment}</span>
                      </div>
                    ))}
                    {ruling && (
                      <div className="text-xs border-l-2 border-gold pl-2">
                        <span className="text-gold font-semibold">Stonky&rsquo;s ruling: {ruling.decision}</span>
                        <p className="text-slate-400">{ruling.reasoning}</p>
                      </div>
                    )}
                    {p.risk_report && (
                      <div className="text-xs space-y-0.5">
                        <div className={`font-semibold ${p.risk_report.allowed ? "text-up" : "text-down"}`}>
                          risk engine: {p.risk_report.allowed ? "PASS" : "REJECTED"}
                        </div>
                        {p.risk_report.checks.map((c) => (
                          <div key={c.name} className="font-mono">
                            <span className={c.ok ? "text-up" : "text-down"}>{c.ok ? "✓" : "✗"}</span>{" "}
                            <span className="text-slate-500">{c.name}:</span>{" "}
                            <span className="text-slate-400">{c.detail}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {proposals.length === 0 && <div className="text-slate-600 text-sm">no proposals yet</div>}
        </div>
      </div>
    </div>
  );
}
